/*
  DUK „Benachrichtigen, wenn verfügbar“ (Entwurf H4)
  ------------------------------------------------------------------------------------------------
  Markup: snippets/duk-notify.liquid. Der Block „buy_buttons“ wird beim Variantenwechsel neu gerendert, das Element
  kommt dann frisch in die Seite. Dieses Script wird einmal geladen und definiert <duk-notify>.

  - Desktop (ab 700 px): Das Formular klappt in der Spalte auf.
  - Mobil: Das Formular öffnet als Blatt von unten (wird dafür an <body> gehängt, damit kein Elternelement es einsperrt).
  - Senden: POST /apps/wunschliste/notify (App „DUK Wunschliste“, Cloudflare Worker), JSON.
  - Häkchen Newsletter: zusätzlich Anmeldung bei Klaviyo wie im Footer (Liste DE/EN, Double-Opt-in, Quelle „notify“).
  - Eingerichtete Benachrichtigungen merkt sich der Browser (localStorage „duck:notify“), damit der Knopf nach dem
    Neuladen „Benachrichtigung eingerichtet“ zeigt. Ohne Speicher geht alles weiter, nur ohne diese Anzeige.
  - Die Kaufleiste unten (product-sticky-add-to-cart) öffnet das Formular über [data-duk-notify-open].
  - Bot-Schutz: Cloudflare Turnstile (Attribut turnstile-key). Das Script von Cloudflare lädt erst beim Öffnen des
    Formulars; die Prüfung läuft unsichtbar und zeigt nur bei Verdacht ein Kästchen. Kommt kein Token (Script
    blockiert, Turnstile weg), wird trotzdem gesendet – der Worker entscheidet.
*/

const ENDPOINT = '/apps/wunschliste/notify';
const STORE_KEY = 'duk:notify';
const STORE_DAYS = 180;
const MOBILE = '(max-width: 699px)';

const KLAVIYO_URL = 'https://a.klaviyo.com/client/subscriptions';
const KLAVIYO_REVISION = '2026-07-15';
const KLAVIYO_LISTS = { de: 'XTQ9RA', en: 'TrtsgT' };

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TURNSTILE_WAIT_MS = 4000;

const TEXT = {
  de: {
    done: 'Danke. Wir informieren dich, sobald das Produkt wieder verfügbar ist.',
    wished: 'Es steht jetzt auch auf deiner Wunschliste.',
    doneNewsletter: 'Bitte bestätige noch die Newsletter-Anmeldung in der E-Mail, die wir dir gerade geschickt haben.',
    set: 'Benachrichtigung eingerichtet',
    error: 'Das hat nicht geklappt. Bitte versuche es noch einmal.',
    email: 'Bitte gib eine gültige E-Mail-Adresse ein.'
  },
  en: {
    done: 'Thank you. We’ll let you know as soon as it is available again.',
    wished: 'It is now on your wishlist, too.',
    doneNewsletter: 'Please confirm your newsletter subscription in the email we just sent you.',
    set: 'Notification set',
    error: 'That didn’t work. Please try again.',
    email: 'Please enter a valid email address.'
  }
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function readStore() {
  try {
    const data = JSON.parse(localStorage.getItem(STORE_KEY) || '{}');
    return data && typeof data === 'object' ? data : {};
  } catch (error) {
    return {};
  }
}

function remember(variantId) {
  try {
    const data = readStore();
    const cutoff = Date.now() - STORE_DAYS * 864e5;
    for (const key of Object.keys(data)) if (!(Number(data[key]) > cutoff)) delete data[key];
    data[variantId] = Date.now();
    localStorage.setItem(STORE_KEY, JSON.stringify(data));
  } catch (error) {
    /* private mode: no memory, nothing else changes */
  }
}

function isRemembered(variantId) {
  const value = Number(readStore()[variantId]);
  return value > Date.now() - STORE_DAYS * 864e5;
}

// Cloudflare Turnstile: script once per page, only when a form is opened
let turnstileLoading = null;
function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!turnstileLoading) {
    turnstileLoading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = TURNSTILE_SRC;
      script.async = true;
      script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile missing')));
      script.onerror = () => reject(new Error('turnstile blocked'));
      document.head.append(script);
    }).catch((error) => {
      turnstileLoading = null;
      throw error;
    });
  }
  return turnstileLoading;
}

async function subscribeNewsletter({ siteId, email, locale }) {
  const response = await fetch(`${KLAVIYO_URL}?company_id=${encodeURIComponent(siteId)}`, {
    method: 'POST',
    headers: { accept: 'application/vnd.api+json', 'content-type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
    body: JSON.stringify({
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'notify',
          profile: { data: { type: 'profile', attributes: { email, locale, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } }
        },
        relationships: { list: { data: { type: 'list', id: locale === 'de' ? KLAVIYO_LISTS.de : KLAVIYO_LISTS.en } } }
      }
    })
  });

  if (!response.ok) throw new Error(`klaviyo ${response.status}`);
}

class DukNotify extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.locale = this.getAttribute('locale') === 'de' ? 'de' : 'en';
    this.text = TEXT[this.locale];
    this.openButton = this.querySelector('.duk-notify__open');
    this.openButton?.setAttribute('aria-expanded', 'false');
    this.panel = this.querySelector('.duk-notify__panel');
    this.form = this.querySelector('.duk-notify__form');
    this.errorLine = this.querySelector('.duk-notify__error');
    this.doneLine = this.querySelector('.duk-notify__done');
    this.captchaBox = this.querySelector('[data-duk-notify-captcha]');
    this.captchaToken = '';
    this.captchaId = null;
    this.backdrop = null;

    this.onKey = (event) => {
      if (event.key === 'Escape') this.close(true);
    };

    this.openButton?.addEventListener('click', () => (this.panel.hidden ? this.open() : this.close(true)));
    this.panel?.querySelector('[data-duk-notify-close]')?.addEventListener('click', () => this.close(true));
    // No own <form> (we sit inside the product form): send on click and on Enter in the field
    this.form?.querySelector('[data-duk-notify-submit]')?.addEventListener('click', (event) => this.submit(event));
    this.form?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && event.target instanceof HTMLInputElement && event.target.type === 'email') {
        event.preventDefault();
        this.submit(event);
      }
    });

    if (isRemembered(this.variantId)) this.markSet();

    // „Auf die Wunschliste setzen“ nur, wenn das Herz auf der Seite steht und das Produkt noch nicht gemerkt ist
    this.wishRow = this.form?.querySelector('[data-duk-notify-wish]');
    // (geprüft beim Öffnen: das Herz steht im Seitenaufbau oft erst nach diesem Element)

    DukNotify.current = this;

    // Von der Produktkarte („Benachrichtigen“ bzw. Glocke): ?notify=1 öffnet den Aufklapper gleich beim Laden
    if (!DukNotify.autoOpened && new URLSearchParams(window.location.search).has('notify')) {
      DukNotify.autoOpened = true;
      window.setTimeout(() => {
        if (!this.isConnected || !this.panel.hidden) return;
        if (!this.mobile) this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.open();
      }, 400);
    }
  }

  disconnectedCallback() {
    // A moved panel (sheet on mobile) stays with this element: the block is re-rendered on variant change
    if (this.isConnected) return;
    if (this.panel && this.panel.parentNode && this.panel.parentNode !== this) this.panel.remove();
    this.backdrop?.remove();
    document.removeEventListener('keydown', this.onKey);
    document.documentElement.classList.remove('duk-notify-lock');
    if (this.captchaId !== null && window.turnstile) {
      try {
        window.turnstile.remove(this.captchaId);
      } catch (error) {
        /* already gone */
      }
    }
    if (DukNotify.current === this) DukNotify.current = null;
  }

  get variantId() {
    return this.getAttribute('variant-id');
  }

  get wishButton() {
    const id = this.getAttribute('product-id');
    return document.querySelector(`duk-wishlist-button[product-id="${id}"] button`) || document.querySelector('.duk-wishlist-product duk-wishlist-button button');
  }

  get mobile() {
    return window.matchMedia(MOBILE).matches;
  }

  // Turnstile widget in the panel: invisible unless Cloudflare wants a click
  prepareCaptcha() {
    const key = this.getAttribute('turnstile-key');
    if (!key || !this.captchaBox || this.captchaId !== null || this._captchaStarting) return;
    this._captchaStarting = true;
    loadTurnstile()
      .then((turnstile) => {
        if (!this.isConnected) return;
        this.captchaId = turnstile.render(this.captchaBox, {
          sitekey: key,
          action: 'notify',
          appearance: 'interaction-only',
          size: 'flexible',
          language: this.locale,
          callback: (token) => {
            this.captchaToken = token;
          },
          'expired-callback': () => {
            this.captchaToken = '';
          },
          'error-callback': () => {
            this.captchaToken = '';
            return true;
          }
        });
      })
      .catch((error) => console.warn('DUK notify turnstile:', error && error.message ? error.message : error))
      .finally(() => {
        this._captchaStarting = false;
      });
  }

  // Wait briefly for the token (it usually arrives while the email is typed)
  async captcha() {
    if (!this.getAttribute('turnstile-key')) return '';
    const until = Date.now() + TURNSTILE_WAIT_MS;
    while (!this.captchaToken && Date.now() < until && (this.captchaId !== null || this._captchaStarting)) {
      await new Promise((resolve) => setTimeout(resolve, 150));
    }
    return this.captchaToken;
  }

  resetCaptcha() {
    this.captchaToken = '';
    if (this.captchaId !== null && window.turnstile) {
      try {
        window.turnstile.reset(this.captchaId);
      } catch (error) {
        /* nothing to reset */
      }
    }
  }

  open() {
    if (!this.panel) return;

    if (this.mobile) {
      this.backdrop = this.backdrop || Object.assign(document.createElement('div'), { className: 'duk-notify__backdrop' });
      this.backdrop.onclick = () => this.close(true);
      document.body.append(this.backdrop, this.panel);
      this.panel.classList.add('is-sheet');
      document.documentElement.classList.add('duk-notify-lock');
      this.panel.setAttribute('aria-modal', 'true');
    } else {
      this.panel.classList.remove('is-sheet');
      this.panel.removeAttribute('aria-modal');
    }

    if (this.wishRow) this.wishRow.hidden = !this.wishButton || this.wishButton.getAttribute('aria-pressed') === 'true';
    this.panel.hidden = false;
    this.openButton?.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', this.onKey);
    this.prepareCaptcha();

    requestAnimationFrame(() => {
      this.panel.classList.add('is-open');
      this.backdrop?.classList.add('is-open');
      // Not into the email field: the browser would open its address suggestions right away (Matthias, 12.10.)
      this.panel.querySelector('[data-duk-notify-close]')?.focus({ preventScroll: true, focusVisible: false });
    });
  }

  close(returnFocus) {
    if (!this.panel || this.panel.hidden) return;

    this.panel.classList.remove('is-open');
    this.backdrop?.classList.remove('is-open');
    this.panel.hidden = true;
    this.openButton?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', this.onKey);
    document.documentElement.classList.remove('duk-notify-lock');

    if (this.panel.parentNode !== this) this.append(this.panel);
    this.backdrop?.remove();
    this.panel.classList.remove('is-sheet');

    if (returnFocus) this.openButton?.focus({ preventScroll: true });
  }

  markSet() {
    const label = this.openButton?.querySelector('.button__content');
    if (label) {
      label.textContent = this.text.set;
      ['rotated-text', 'up-text'].forEach((name) => label.hasAttribute(name) && label.setAttribute(name, this.text.set));
    }
    this.openButton?.classList.add('is-set');
    document.querySelectorAll(`[data-duk-notify-open="${this.variantId}"]`).forEach((button) => {
      button.querySelectorAll('.button__content').forEach((content) => (content.textContent = this.text.set));
    });
  }

  showError(message) {
    if (!this.errorLine) return;
    this.errorLine.textContent = message;
    this.errorLine.hidden = false;
  }

  async submit(event) {
    event.preventDefault();
    if (this._busy) return;

    const field = this.form.querySelector('input[name="email"]');
    const email = (field?.value || '').trim();
    const newsletter = Boolean(this.form.querySelector('input[name="newsletter"]')?.checked);
    const wishlist = Boolean(this.wishRow && !this.wishRow.hidden && this.wishRow.querySelector('input')?.checked);
    const submitButton = this.form.querySelector('[data-duk-notify-submit]');

    this.errorLine.hidden = true;

    if (!EMAIL_PATTERN.test(email) || email.length > 254) {
      field?.setAttribute('aria-invalid', 'true');
      this.showError(this.text.email);
      field?.focus();
      return;
    }

    field?.removeAttribute('aria-invalid');
    this._busy = true;
    submitButton?.setAttribute('aria-busy', 'true');
    if (submitButton) submitButton.disabled = true;

    const captcha = await this.captcha();

    const body = {
      v: 1,
      email,
      variant: Number(this.variantId),
      product: Number(this.getAttribute('product-id')),
      locale: this.locale,
      country: this.getAttribute('country') || '',
      origin: window.location.origin,
      root: this.getAttribute('root') || '/',
      newsletter,
      ...(captcha ? { captcha } : {})
    };

    const newsletterWork = newsletter
      ? subscribeNewsletter({ siteId: this.getAttribute('site-id'), email, locale: this.locale }).then(() => true, (error) => {
          console.error('DUK notify newsletter:', error);
          return false;
        })
      : Promise.resolve(false);

    try {
      const response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body)
      });

      if (!response.ok) throw new Error(`notify ${response.status}`);

      const subscribed = await newsletterWork;
      remember(this.variantId);

      this.form.hidden = true;
      this.doneLine.textContent = this.text.done;
      if (wishlist) {
        const heart = this.wishButton;
        if (heart && heart.getAttribute('aria-pressed') !== 'true') heart.click();
        this.doneLine.textContent += ` ${this.text.wished}`;
      }
      if (subscribed) this.doneLine.textContent += ` ${this.text.doneNewsletter}`;
      this.doneLine.hidden = false;
      this.markSet();
      this.doneLine.setAttribute('tabindex', '-1');
      this.doneLine.focus({ preventScroll: true });
    } catch (error) {
      console.error('DUK notify:', error);
      this.showError(this.text.error);
      // A Turnstile token is valid once: fresh one for the next try
      this.resetCaptcha();
    } finally {
      this._busy = false;
      submitButton?.removeAttribute('aria-busy');
      if (submitButton) submitButton.disabled = false;
    }
  }
}

if (!customElements.get('duk-notify')) customElements.define('duk-notify', DukNotify);

// Sticky purchase bar: open the form of the main product
if (!window.DukNotifyStickyBound) {
  window.DukNotifyStickyBound = true;

  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-duk-notify-open]') : null;
    if (!trigger) return;

    event.preventDefault();
    const element = document.querySelector(`duk-notify[variant-id="${trigger.getAttribute('data-duk-notify-open')}"]`) || DukNotify.current;
    if (!element) return;

    if (!element.mobile) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    element.open();
  });
}
